import { useMemo } from 'react';
import { History, ArrowRight, PlusCircle, Pencil } from 'lucide-react';
import type { LogbookTransition } from '../../types/logbook';
import { logbookFieldLabel } from '../../lib/api/mappers';
import { Spinner } from '../ui/Spinner';
import { formatDateTime } from '../../utils/trips';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';

// issue #112: `changes` trae ids/timestamps crudos porque así los emite el
// backend a propósito (el valor auditado es el id, no la etiqueta del momento
// en que se pinta). Este componente se abre desde tres páginas distintas
// (Novedades, y el expediente de viaje desde Viajes/Operador/Reportes) y solo
// una de ellas ya tenía el catálogo en memoria — pedirlo aquí, self-contained
// con los mismos hooks, evita tener que perforar `types`/`vehicles`/`drivers`
// a través de componentes que no los necesitan para nada más. Es un catálogo
// de unas pocas filas que cambia una vez al año: repetir el fetch al abrir un
// detalle es más barato que la alternativa. Un id que no aparece en el mapa
// (borrado, u otro campo sin catálogo como `trip_id`) cae al mismo
// truncado+`title` que ya usa `actor` más abajo.
function resolverValor(
  field: string,
  valor: string,
  maps: { tipos: Map<string, string>; vehiculos: Map<string, string>; conductores: Map<string, string> },
): { texto: string; title?: string } {
  if (!valor) return { texto: '—' };
  if (field === 'entry_type_id' || field === 'entryTypeId') {
    return { texto: maps.tipos.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'vehicle_id' || field === 'vehicleId') {
    return { texto: maps.vehiculos.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'driver_id' || field === 'driverId') {
    return { texto: maps.conductores.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'occurredAt') {
    return { texto: formatDateTime(valor) };
  }
  if (field === 'trip_id' || field === 'tripId') {
    return { texto: valor.slice(0, 8), title: valor };
  }
  return { texto: valor };
}

/**
 * FE-13 — los cambios de una novedad: append-only, garantizados por la AUSENCIA
 * del privilegio `UPDATE` en la base. Ni el core puede reescribirla.
 *
 * Lo que hace que valga la pena pintarla es el "de qué a qué" de cada campo.
 * Una novedad que solo dijera *"alguien corrigió esto el martes"* no sirve
 * para lo único que se le va a pedir: entender qué decía antes el relato de un
 * accidente y quién lo cambió.
 *
 * ⚠️ Una asociación retirada llega con `to` vacío. Se pinta como "—" y no como
 * cadena vacía: un hueco en blanco se lee como un fallo de render, y este es
 * un dato que alguien decidió quitar.
 */
export function LogbookTransitions({
  transitions,
  isLoading,
  error,
}: {
  transitions: LogbookTransition[];
  isLoading: boolean;
  error: Error | null;
}) {
  const { types } = useLogbookTypes();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const maps = useMemo(() => ({
    tipos: new Map(types.map(t => [t.id, t.label])),
    vehiculos: new Map(vehicles.map(v => [v.id, v.plate])),
    conductores: new Map(drivers.map(d => [d.id, d.name])),
  }), [types, vehicles, drivers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <History size={15} style={{ color: 'var(--accent)' }} />
        Cambios de la novedad
      </h4>

      {isLoading && (
        <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>
      )}

      {!isLoading && error && (
        <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>
          No se pudieron cargar los cambios: {error.message}
        </p>
      )}

      {!isLoading && !error && transitions.length === 0 && (
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Sin anotaciones.</p>
      )}

      {!isLoading && !error && transitions.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {transitions.map((t, i) => {
            const esCorreccion = t.action === 'CORRECTED';
            const color = esCorreccion ? '#F59E0B' : '#10B981';
            return (
              <li
                key={`${t.recordedAt}-${i}`}
                style={{
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 'var(--radius-md)',
                  padding: '0.625rem 0.875rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', fontWeight: 800, color }}>
                  {esCorreccion ? <Pencil size={12} /> : <PlusCircle size={12} />}
                  {esCorreccion ? 'Corrección' : 'Registro inicial'}
                </div>

                {t.changes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {t.changes.map(c => {
                      const desde = resolverValor(c.field, c.from, maps);
                      const hasta = resolverValor(c.field, c.to, maps);
                      return (
                        <div key={c.field} style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{logbookFieldLabel(c.field)}:</strong>
                          <span style={{ textDecoration: 'line-through', opacity: 0.75 }} title={desde.title}>{desde.texto}</span>
                          <ArrowRight size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }} title={hasta.title}>{hasta.texto}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {t.reason && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>“{t.reason}”</p>
                )}

                {/* Mismo límite que la novedad del viaje: el contrato entrega
                    el `actor` como userId crudo y no hay endpoint que lo
                    resuelva a nombre sin permisos de admin (PENDIENTES.md). */}
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }} title={t.actor}>
                  {formatDateTime(t.recordedAt)} · {t.actor === 'system' ? 'automático' : `usuario ${t.actor.slice(0, 8)}`}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
